// End-of-play feedback survey for nerdle labs. Schema-driven (like maffdoku's
// Survey system) so questions are declarative; the SurveyScreen component renders
// whatever this describes. Responses land in the same `surveyResponses` collection
// (→ BigQuery) as consent, tagged with the anonymous `sessionId` so each response
// JOINS to that player's transcripts/traces — the whole point of collecting both.

import { addDoc, collection, serverTimestamp } from 'firebase/firestore'
import { db } from '../../index'

export type SurveyQuestion =
  | { id: string; type: 'single-choice'; question: string; options: string[]; required?: boolean; sectionBefore?: string }
  | { id: string; type: 'multi-choice'; question: string; options: string[]; required?: boolean; sectionBefore?: string }
  | { id: string; type: 'scale'; question: string; min: number; max: number; minLabel?: string; maxLabel?: string; required?: boolean; sectionBefore?: string }
  | { id: string; type: 'text'; question: string; placeholder?: string; multiline?: boolean; required?: boolean; sectionBefore?: string }

export type SurveySchema = {
  id: string
  title: string
  intro?: string
  questions: SurveyQuestion[]
  submitText?: string
  thankYouText?: string
}

export type SurveyResponse = {
  surveyId: string
  answers: Record<string, string | number | string[] | null>
  hiddenFields: Record<string, unknown>
  submittedAt: string
}

// ---------------------------------------------------------------------------
// The schema. ~11 short questions, all optional, ~2 min. Grouped so players know
// what they're being asked about. Focus is on the SUBJECTIVE experience of the
// think-aloud mechanic + research covariates — things the behavioural traces
// can't tell us (traces already capture whether/how much they actually talked).
export const labsFeedbackSurvey: SurveySchema = {
  id: 'labs-feedback-v1',
  title: 'Quick feedback',
  intro:
    "You just played a new kind of puzzle — one you solve out loud. A couple of minutes of feedback would really help our research. It's anonymous, and every question is optional.",
  submitText: 'Send feedback',
  thankYouText: 'Thank you — this really helps our research.',
  questions: [
    {
      id: 'talkedAloud',
      type: 'single-choice',
      sectionBefore: 'Thinking out loud',
      question: 'Did you talk out loud while solving?',
      options: ['I talked through most puzzles', 'I talked a bit', 'I mostly stayed quiet', 'I didn’t really talk'],
    },
    {
      id: 'talkingFelt',
      type: 'scale',
      question: 'How did thinking out loud feel?',
      min: 1,
      max: 7,
      minLabel: 'Awkward',
      maxLabel: 'Natural',
    },
    {
      id: 'talkingEffect',
      type: 'single-choice',
      question: 'Did talking out loud change how you solved?',
      options: ['It helped me', 'No real effect', 'It distracted me'],
    },
    {
      id: 'heardYou',
      type: 'scale',
      question: 'Did the game understand what you said?',
      min: 1,
      max: 7,
      minLabel: 'Rarely',
      maxLabel: 'Almost always',
    },
    {
      id: 'playAgain',
      type: 'single-choice',
      question: 'Would you play a talk-aloud puzzle again?',
      options: ['Yes, definitely', 'Yes, probably', 'Not sure', 'Probably not', 'No'],
    },
    {
      id: 'instructions',
      type: 'scale',
      sectionBefore: 'The game',
      question: 'How easy was it to understand the instructions and controls?',
      min: 1,
      max: 7,
      minLabel: 'Confusing',
      maxLabel: 'Crystal clear',
    },
    {
      id: 'enjoyment',
      type: 'scale',
      question: 'How enjoyable was it?',
      min: 1,
      max: 7,
      minLabel: 'Not at all',
      maxLabel: 'Loved it',
    },
    {
      id: 'challenge',
      type: 'scale',
      question: 'How challenging was it?',
      min: 1,
      max: 7,
      minLabel: 'Too easy',
      maxLabel: 'Too hard',
    },
    {
      id: 'firstLanguage',
      type: 'single-choice',
      sectionBefore: 'A little about you',
      question: 'Is English your first language?',
      options: ['Yes', 'No, but I’m fluent', 'No'],
    },
    {
      id: 'ageBand',
      type: 'single-choice',
      question: 'Which age group are you in?',
      options: ['Under 18', '18-24', '25-34', '35-44', '45-54', '55-64', '65-74', '75+', 'Prefer not to say'],
    },
    {
      id: 'improve',
      type: 'text',
      sectionBefore: 'Anything else?',
      question: 'What would make this better?',
      multiline: true,
      placeholder: 'Any thoughts on the game or on talking out loud — bugs, frustrations, ideas.',
    },
  ],
}

// ---------------------------------------------------------------------------
// localStorage dedup so a returning player who retakes the test isn't re-surveyed.
const completedKey = (surveyId: string) => 'labs_survey_' + surveyId
export const surveyCompletedAt = (surveyId: string): string | null => {
  try {
    return typeof window === 'undefined' ? null : localStorage.getItem(completedKey(surveyId))
  } catch {
    return null
  }
}
export const markSurveyDone = (surveyId: string): void => {
  try {
    if (!localStorage.getItem(completedKey(surveyId))) localStorage.setItem(completedKey(surveyId), new Date().toISOString())
  } catch {
    /* private mode */
  }
}

// ---------------------------------------------------------------------------
// Hidden fields: the sessionId join key + a few cheap join/segmentation signals.
// Kept BQ-safe (string / number / boolean / null).
async function sha256Hex(text: string): Promise<string> {
  if (typeof crypto === 'undefined' || !crypto.subtle) return ''
  try {
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
  } catch {
    return ''
  }
}
function gaClientId(): string | null {
  if (typeof document === 'undefined') return null
  const m = document.cookie.match(/(?:^|;\s*)_ga=GA\d\.\d+\.([\d.]+)/)
  return m ? m[1] : null
}

export async function collectSurveyHiddenFields(sessionId: string): Promise<Record<string, unknown>> {
  if (typeof window === 'undefined') return { sessionId }
  const lblToken = (() => {
    try {
      return localStorage.getItem('lbl_token') || ''
    } catch {
      return ''
    }
  })()
  return {
    sessionId, // ← join key to this player's labsTraces / labsRetros
    gameType: 'labs-24',
    gaClientId: gaClientId(), // → GA4 BQ export (user_pseudo_id)
    lblTokenHash: lblToken ? (await sha256Hex(lblToken)) || null : null, // → account data, if logged in
    isRegistered: !!lblToken,
    language: navigator.language || '',
    timezone: (() => {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone
      } catch {
        return ''
      }
    })(),
    referrer: (document.referrer || '').slice(0, 200),
    userAgent: (navigator.userAgent || '').slice(0, 200),
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
  }
}

// One create-only write per submission → surveyResponses → BigQuery.
export async function submitSurvey(response: SurveyResponse): Promise<string> {
  const doc = await addDoc(collection(db, 'surveyResponses'), {
    ...response,
    serverReceivedAt: serverTimestamp(),
  })
  return doc.id
}
